import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Link, useNavigate } from 'react-router-dom';
import { Table, Button, Modal, OverlayTrigger, Tooltip } from 'react-bootstrap';

import { fetchContainers, deleteContainer } from '../../../store/actions/container';
import CustomModal from '../../../hoc/customModal';
import EditContainer from './edit';
import AddContainer from './add';

// Utils
import { Loader, isSubscriptionActive, shouldShowPaymentAlert } from '../../../utils/tools';
import PaymentAlert from '../../../utils/paymentAlert';
import PlanLimitAlert from '../../../utils/planLimitAlert';

import { PLAN_LIMITS } from '../../../config/plans'; 

// Assets
import { ReactComponent as EditIcon } from '../../../assets/edit.svg';
import { ReactComponent as RemoveIcon } from '../../../assets/remove.svg';

const Containers = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const containers = useSelector(state => state.branch.container);
    const currentBranch = useSelector(state => state.branch.current);
    const deletedContainer = useSelector(state => state.branch.deletedContainer);
    const tenant = useSelector(state => state.tenant.current);

    const planLimits = PLAN_LIMITS[tenant.plan];
    const isActive = isSubscriptionActive(tenant?.subscriptionStatus, tenant?.plan);
    const isAtLimit = !!(tenant?.plan && containers && Array.isArray(containers) && containers.length >= planLimits?.maxContainers);

    // Custom modal
    const [isEditModalVisible, setIsEditModalVisible] = useState(false);
    const [isAddModalVisible, setIsAddModalVisible] = useState(false); // Nový stav pre pridanie nádoby
    const [containerToEdit, setContainerToEdit] = useState(null);

    // Confirmation modal state
    const [showConfirmModal, setShowConfirmModal] = useState(false);
    const [containerToDelete, setContainerToDelete] = useState(null);

    useEffect(() => {
        if (currentBranch !== '') dispatch(fetchContainers(currentBranch));
    }, [dispatch, currentBranch, deletedContainer]);

    // Handlers
    const handleEditButtonClick = (container) => {
        setContainerToEdit(container);
        setIsEditModalVisible(true);
    };

    const handleCloseEditModal = () => {
        setIsEditModalVisible(false);
        setContainerToEdit(null);
    };

    const handleCloseAddModal = () => {
        setIsAddModalVisible(false);
    };

    const handleDeleteButtonClick = (container) => {
        setContainerToDelete(container);
        setShowConfirmModal(true);
    };

    const handleCloseConfirmModal = () => {
        setShowConfirmModal(false);
        setContainerToDelete(null);
    };

    const handleConfirmDelete = () => {
        if (containerToDelete && currentBranch !== '') {
            dispatch(deleteContainer({ branchId: currentBranch, container: containerToDelete }))
                .then(() => {
                    if (currentBranch !== '') {
                        dispatch(fetchContainers(currentBranch));
                    }
                    setShowConfirmModal(false);
                })
                .catch(error => {
                    console.error('Error deleting container:', error);
                    setShowConfirmModal(false);
                });
        }
    };

    // Render Tooltips
    const renderTooltipEdit = (props) => (
        <Tooltip id="button-tooltip" {...props}>
            Upraviť nádobu
        </Tooltip>
    );

    const renderTooltipRemove = (props) => (
        <Tooltip id="button-tooltip" {...props}>
            Vymazať nádobu
        </Tooltip>
    );

    return (
        <>
            {currentBranch.loading ? (
                <Loader />
            ) : (
                <div>
                    <Button 
                        className="button-custom-large radius"
                        onClick={() => setIsAddModalVisible(true)}
                        disabled={tenant.plan && containers && Array.isArray(containers) && containers.length >= planLimits.maxContainers || !isActive}
                    >
                        Pridať nádobu
                    </Button>

                    {tenant.plan && containers && Array.isArray(containers) && containers.length >= planLimits.maxContainers && (
                        <PlanLimitAlert
                            current={containers.length}
                            max={planLimits.maxContainers}
                            label="nádob"
                            plan={tenant.plan}
                        />
                    )}

                    {tenant && !isAtLimit && shouldShowPaymentAlert(tenant.subscriptionStatus, tenant.plan) && (
                        <PaymentAlert status={tenant.subscriptionStatus} plan={tenant.plan} />
                    )}

                    <div className="content pt-4 pb-4">
                        <Table responsive hover>
                            <thead>
                                <tr>
                                    <th className="px-3">Nádoba</th>
                                    <th>Akcia</th>
                                </tr>
                            </thead>
                            <tbody>
                                {Array.isArray(containers) && containers.length === 0 && (
                                    <tr>
                                        <td colSpan={2} className="text-center text-muted py-5">
                                            <p className="mb-1"><strong>Zatiaľ nemáte žiadnu nádobu.</strong></p>
                                            <p className="mb-0">Kliknutím na tlačidlo <em>Pridať nádobu</em> vytvoríte prvú nádobu.</p>
                                        </td>
                                    </tr>
                                )}
                                {Array.isArray(containers) && containers.map((container) => (
                                    <tr key={containers.indexOf(container)}>
                                        <td className="px-3">{container}</td>
                                        <td>

                                            {/* Edit Icon */}
                                            <Button
                                                className="btnIcon"
                                            >
                                                <OverlayTrigger placement="top" overlay={renderTooltipEdit}>
                                                    <EditIcon
                                                        onClick={() => handleEditButtonClick(container)}
                                                        alt="Upraviť"
                                                        className="icon icon-edit"
                                                    />
                                                </OverlayTrigger>
                                            </Button>

                                            {/* RemoveIcon */}
                                            <Button
                                                className="btnIcon"
                                            >
                                                <OverlayTrigger placement="top" overlay={renderTooltipRemove}>
                                                    <RemoveIcon
                                                        onClick={() => handleDeleteButtonClick(container)}
                                                        alt="Vymazať"
                                                        className="icon icon-remove"
                                                    />
                                                </OverlayTrigger>
                                            </Button>

                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    </div>

                    {/* Custom modal */}
                    <CustomModal isVisible={isEditModalVisible} onClose={handleCloseEditModal}>
                        {
                            containerToEdit && isEditModalVisible ?
                                <EditContainer container={containerToEdit} onClose={handleCloseEditModal} />
                            : null
                        }
                        <AddContainer isVisible={isAddModalVisible} onClose={handleCloseAddModal} />
                    </CustomModal>

                    {/* Confirmation modal */}
                    <Modal
                        show={showConfirmModal}
                        onHide={handleCloseConfirmModal}
                        centered
                    >
                        <Modal.Header closeButton>
                            <Modal.Title>Potvrdenie</Modal.Title>
                        </Modal.Header>
                        <Modal.Body>Naozaj chcete vymazať nádobu <b>{containerToDelete}</b>?</Modal.Body>
                        <Modal.Footer>
                            <Button variant="secondary" onClick={handleCloseConfirmModal}>
                                Zrušiť
                            </Button>
                            <Button variant="danger" onClick={handleConfirmDelete}>
                                Áno, vymazať
                            </Button>
                        </Modal.Footer>
                    </Modal>
                </div>
            )}
        </>
    );
};

export default Containers;
